import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CTableWrapper,
    DBlock,
    DConfirmationModal,
    DFab,
    DPage,
    DPageBody,
    DeleteIcon,
    EditIcon,
} from '../../../dum';
import { Box, IconButton, Typography } from '@mui/material';
import {
    GetPayrollPeriodsQuery,
    useGetPayrollPeriodsQuery,
    useDeletePayrollPeriodMutation,
    useOrderProductionBranchInputQuery,
} from '../../../../services';
import { formatDate } from '../../../../helpers';
import { SUpsertPayrollPeriodDialog } from '../../../smart';

type TPayrollPeriod = GetPayrollPeriodsQuery['getPayrollPeriods'][number];

export function PayrollPeriodsPage() {
    const [upsertOpen, setUpsertOpen] = useState(false);
    const [periodId, setPeriodId] = useState<number | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<TPayrollPeriod | null>(null);
    const navigate = useNavigate();

    const { data, refetch, loading } = useGetPayrollPeriodsQuery({
        variables: { branch_id: null },
    });

    const { data: branchData } = useOrderProductionBranchInputQuery();

    const [deleteMutation] = useDeletePayrollPeriodMutation();

    const periods = data?.getPayrollPeriods ?? [];
    const branches = branchData?.getBranches ?? [];

    const handleEdit = (period: TPayrollPeriod) => {
        setPeriodId(period.id);
        setUpsertOpen(true);
    };

    const handleDeleteConfirmed = async () => {
        if (!deleteTarget) return;
        await deleteMutation({ variables: { PayrollPeriodId: deleteTarget.id } });
        setDeleteTarget(null);
        await refetch();
    };

    return (
        <DPage>
            <DFab onClick={() => { setPeriodId(null); setUpsertOpen(true); }} />
            <DPageBody>
                <DBlock>
                    <CTableWrapper
                        data={periods}
                        loading={loading}
                        stickyHeader
                        items={[
                            {
                                title: 'Semana',
                                content: (p) => (
                                    <Typography
                                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                                        onClick={() => navigate(`/payroll/${p.id}`)}
                                    >
                                        Semana {p.week_number}
                                    </Typography>
                                ),
                            },
                            {
                                title: 'Sucursal',
                                content: (p) =>
                                    branches.find((b) => b.id === p.branch_id)?.name ?? '—',
                            },
                            {
                                title: 'Inicio',
                                content: (p) => formatDate(p.start_date),
                            },
                            {
                                title: 'Fin',
                                content: (p) => formatDate(p.end_date),
                            },
                            {
                                title: '',
                                content: (p) => (
                                    <Box display="flex">
                                        <IconButton size="small" onClick={() => handleEdit(p)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" onClick={() => setDeleteTarget(p)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                ),
                            },
                        ]}
                    />
                </DBlock>
            </DPageBody>
            <SUpsertPayrollPeriodDialog
                open={upsertOpen}
                setOpen={setUpsertOpen}
                periodId={periodId}
                onSuccess={() => refetch()}
            />
            <DConfirmationModal
                open={!!deleteTarget}
                setOpen={(open) => { if (!open) setDeleteTarget(null); }}
                callback={handleDeleteConfirmed}
            />
        </DPage>
    );
}
